import React, {useEffect, useMemo, useState} from "react";
import throttle from "lodash/throttle";
import {useDocumentTranslation} from "../documents/documentContext";
import Autocomplete from "@mui/material/Autocomplete";
import {Grid} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useTheme} from "@emotion/react";
import {If} from "../app/If";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import AddIcon from '@mui/icons-material/Add';

export const KulturNavSearch = ({
                                    id,
                                    searchHandler,
                                    onChange,
                                    onClear,
                                    disabled,
                                    inputLabel,
                                    Icon,
                                    value,
                                    multiple = true,
                                    helperText = null,
                                    size = null,
                                    inputVariant = 'outlined'
                                }) => {
    const theme = useTheme();
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const t = useDocumentTranslation();

    const fetchOptions = useMemo(
        () =>
            throttle((request, callback) => {
                const params = request
                    .split(" ")
                    .map((word) => `${word}*`)
                    .join(" ");
                setOptions([]);
                setSearching(true);
                searchHandler(params, (data) => callback(data));
            }, 400),
        [searchHandler]
    );

    const populateOptions = (results) => {
        const newOptions = results || [];
        setOptions(newOptions);
        setSearching(false);
    };

    useEffect(() => {
        let active = true;

        if (inputValue === "") {
            setOptions([]);
            return undefined;
        }

        fetchOptions(inputValue, (results) => {
            if (active) {
                populateOptions(results);
            }
        });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]); // NO SONAR

    const changeHandler = (_event, val, reason) => {
        if ("selectOption" === reason || "removeOption" === reason) {
            onChange(val);
        } else if ("clear" === reason) {
            onClear();
        }
    };

    const getOptionLabelTitle = (option) => {
        let title = option?.content?.nameAndTitle;
        if (typeof (title) === 'undefined') {
            title = option.title || "";
        }
        return title;
    };

    const getDistinctOptions = (opts, params) => {
        if (searching || "" === params.inputValue) {
            return opts;
        }

        // Add a new option "Create.." if an option with the specified input value does not already exist.
        const withAdd = opts;
        if (!opts.map((option) => option.title).includes(params.inputValue)) {
            withAdd.push({
                uniqueId: "",
                title: `${params.inputValue}`,
                create: true,
            });
        }
        return withAdd;
    };

    return (
        <Autocomplete
            id={id}
            freeSolo
            handleHomeEndKeys={true}
            disabled={disabled}
            multiple
            loading={Boolean(searching && inputValue)}
            loadingText={t("searchingPlaceholder", "Søker...")}
            getOptionLabel={getOptionLabelTitle}
            filterOptions={getDistinctOptions}
            options={options}
            includeInputInList={true}
            filterSelectedOptions={true}
            isOptionEqualToValue={(option, val) => {
                return (
                    option.uniqueId !== undefined && option.uniqueId === val.uniqueId
                );
            }}
            slotProps={{
                chip: {
                    color: "secondary",
                }
            }}
            value={value}
            onChange={changeHandler}
            onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={(params) => {
                // Destructure deprecated props so they are never spread onto TextField
                // as top-level props — MUI v9 forwards them to DOM elements otherwise.
                const {InputProps, inputProps: _inputProps, ...textFieldParams} = params;
                return (
                    <TextField
                        variant={inputVariant}
                        {...textFieldParams}
                        label={inputLabel}
                        helperText={helperText}
                        size={size}
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...(params.slotProps?.input ?? InputProps ?? {}),
                            }
                        }}
                    />
                );
            }}
            renderOption={({key, ...props}, option) => {
                // Use sourceId to guarantee a unique key even when two options share the same label.
                const uniqueKey = option.sourceId ? `${key}-${option.sourceId}` : key;
                return (
                    <Grid {...props}
                          container direction="row"
                          key={uniqueKey}
                    >
                        <If boolean={option.create}>
                            <Grid>
                                <AddIcon
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        marginRight: theme.spacing(2),
                                    }}
                                />
                            </Grid>
                            <Grid>
                                <Typography variant="body2" color="textSecondary">
                                    {option.create ? t("createNew", "Opprett") : option.source}
                                </Typography>
                            </Grid>
                        </If>
                        <If boolean={!option.create}>
                            <Grid>
                                <Icon
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        marginRight: theme.spacing(2),
                                    }}
                                />
                            </Grid>
                            <Grid>
                                <div>
                                    <Typography
                                        variant="subtitle1">{typeof (option?.content?.nameAndTitle) !== 'undefined' ? option?.content?.nameAndTitle : option.title}</Typography>
                                </div>
                                <Box
                                    sx={{
                                        maxWidth: "400px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        color: "secondary.main",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color={"secondary"}
                                        noWrap
                                        component={"span"}
                                    >
                                        {option.source}
                                    </Typography>
                                </Box>
                            </Grid>
                        </If>
                    </Grid>
                );
            }}
        />
    );
};
